import { calcularMontosCobertura } from "./copagoCalculo";
import { CONST_VALUE } from "./Global";

/* ============================================================================
   APLICAR PLAN · FASE 3 — el efecto, calculado antes de aplicarlo

   AQUÍ NO HAY NINGUNA REGLA NUEVA. Es, línea por línea, lo que ya hacía
   `handleAgregarPlanes` en ModalPlanes.js: recorrer las líneas de la factura,
   buscar cada `idExamenTipo` entre los `detalles` del plan y, si está,
   sustituir su precio por la parte del paciente que sale de
   `calcularMontosCobertura(tarifa, copago)`.

   LO ÚNICO QUE CAMBIA es CUÁNDO se calcula: antes se aplicaba y se veía el
   resultado después; ahora se calcula primero, se enseña, y se aplica si el
   usuario dice que sí. El backend lo recalcula igual server-side al facturar
   (ControllerBilling), así que esto no decide nada -- sólo lo anticipa.

   APLICAR PLAN NO AGREGA LÍNEAS. `resultado` tiene exactamente la misma
   longitud que la entrada: es un `map`. Si algún día dejara de serlo, el
   modelo de esta pantalla estaría equivocado y habría que rehacerla.
   ========================================================================== */

/** Subtotal con la MISMA fórmula que `useTotalFactura`: Σ(cantidad × precio). */
export function subtotalDe(lineas) {
    return (lineas || []).reduce((acc, p) => {
        const precio = Number(p?.precio);
        const cantidad = Number(p?.cantidad) || 1;
        return acc + (Number.isFinite(precio) ? precio * cantidad : 0);
    }, 0);
}

/**
 * Total como lo pinta el Resumen de la pantalla: subtotal + IVA.
 *
 * Se calcula igual y con la misma constante a propósito. Si la vista previa
 * dijera "el total queda en $106.84" y el Resumen dijera otra cosa al
 * cerrar el modal, la vista previa sería peor que no tenerla.
 */
export function totalDe(lineas) {
    const subtotal = subtotalDe(lineas);
    return subtotal + subtotal * (CONST_VALUE.IVA / 100);
}

/**
 * Qué le pasaría a estas líneas con este plan.
 *
 * @returns {{
 *   resultado: object[],   // las líneas ya modificadas, para despachar
 *   cubiertos: object[],   // las que reciben copago del plan
 *   fuera: object[],       // las que NO están en el plan y no cambian
 *   subtotalAntes, subtotalDespues, totalAntes, totalDespues
 * }}
 */
export function efectoDelPlan(lineas, plan) {
    const entrada = lineas || [];
    const detalles = plan?.detalles || [];

    // Un `Map` por examen: el original hacía un `find` dentro del `map`, que
    // con 380 líneas y un plan de 44 son 16.720 comparaciones. Mismo
    // resultado, y el primer detalle gana igual que ganaba con `find`.
    const porExamen = new Map();
    detalles.forEach((d) => {
        if (!porExamen.has(d.id_tipo_examen)) porExamen.set(d.id_tipo_examen, d);
    });

    const cubiertos = [];
    const fuera = [];

    const resultado = entrada.map((proc) => {
        const detalle = porExamen.get(proc.idExamenTipo);
        if (!detalle) {
            // Las líneas que no están en el plan MANTIENEN su precio. No se
            // tocan ni se sacan de la factura.
            fuera.push(proc);
            return proc;
        }
        const m = calcularMontosCobertura(detalle.tarifario?.precio, detalle.copago);
        const actualizado = {
            ...proc,
            precio: m.precioPaciente,
            plan: {
                ...detalle,
                precio_paciente: m.precioPaciente,
                precio_empresa: m.precioEmpresa,
                precio: m.precioEmpresa,
            },
        };
        cubiertos.push(actualizado);
        return actualizado;
    });

    return {
        resultado,
        cubiertos,
        fuera,
        subtotalAntes: subtotalDe(entrada),
        subtotalDespues: subtotalDe(resultado),
        totalAntes: totalDe(entrada),
        totalDespues: totalDe(resultado),
    };
}

/**
 * El total que TENDRÁ LA FACTURA ENTERA después de aplicar.
 *
 * No es lo mismo que `efecto.totalDespues` cuando se aplica sólo a lo
 * seleccionado: ahí `efecto` habla de las líneas marcadas, pero el total que
 * el usuario va a ver en el Resumen es el de toda la factura. Enseñar el del
 * subconjunto sería enseñar una cifra que nunca va a aparecer en pantalla.
 */
export function totalTrasAplicar(factura, efecto) {
    if (!efecto) return totalDe(factura);
    const cambiadas = new Map(
        efecto.resultado.map((p) => [p.idprocedimiento_cita_paciente, p])
    );
    return totalDe(
        (factura || []).map((p) => cambiadas.get(p.idprocedimiento_cita_paciente) || p)
    );
}

/**
 * ¿Este plan sirve para algo sobre estas líneas?
 *
 * Si ninguna línea está en el plan, aplicarlo no cambiaría nada, y un botón
 * que dice "Aplicar a 0 procedimientos" no debería poder pulsarse.
 */
export function aportaAlgo(efecto) {
    return Boolean(efecto && efecto.cubiertos.length > 0);
}

/**
 * Los planes de la cobertura del paciente.
 *
 * El backend ya filtra por `seguro_id`, y ModalPlanes filtraba OTRA VEZ en el
 * navegador. Se conserva ese segundo filtro -- es la red que impide ofrecer el
 * plan de otra aseguradora si el endpoint devolviera de más -- pero en un solo
 * sitio y con su prueba.
 */
export function planesDeLaCobertura(planes, seguroId) {
    if (!seguroId) return [];
    return (planes || []).filter((p) => p.seguro_id === seguroId);
}
